import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, CurrencyEuroIcon, HomeIcon, LinkIcon, MapPinIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { BuildingOffice2Icon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';

import { formatCurrency, formatNumberWithSpaces } from '../../../../common-utils/formatters';
import type { DpeAd } from '../../../types/ads';
import { Dpe } from '../../common/Dpe';
import { Ges } from '../../common/Ges';
import Popup from '../Popup/Popup.module';

// Alias functions to match original code usage or just use them directly
const formatPrice = (price?: number) => formatCurrency(price || 0);
const numberWithSpaces = (num: number) => formatNumberWithSpaces(num.toString());

interface AdsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ads: DpeAd[];
  isLoading: boolean;
  error: string | null;
}

const AdsModal: React.FC<AdsModalProps> = ({ isOpen, onClose, ads, isLoading, error }) => {
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openImageSlideshow = (images: string[], startIndex: number = 0) => {
    setSelectedImages(images);
    setCurrentImageIndex(startIndex);
  };

  const closeImageSlideshow = () => {
    setSelectedImages([]);
    setCurrentImageIndex(0);
  };

  const goToPreviousImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : selectedImages.length - 1));
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => (prev < selectedImages.length - 1 ? prev + 1 : 0));
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImages.length === 0) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPreviousImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextImage();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeImageSlideshow();
      }
    };

    if (selectedImages.length > 0) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImages.length, currentImageIndex]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getPropertyTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      apartment: 'Appartement',
      house: 'Maison',
    };
    return types[type] || type;
  };

  const getTransactionTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      sell: 'Vente',
      rent: 'Location',
    };
    return types[type] || type;
  };

  // Show ads modal only when no images are being viewed
  const showAdsModal = isOpen && selectedImages.length === 0;
  // Show image modal only when images are being viewed
  const showImageModal = selectedImages.length > 0;

  return (
    <>
      {/* Ads Modal */}
      <Popup
        isOpen={showAdsModal}
        onClose={onClose}
        title={
          <div>
            <h3 className="text-lg leading-6 font-semibold text-gray-900">
              Annonces avec ce DPE
            </h3>
            <p className="mt-1 text-sm font-normal text-gray-500">
              Ces annonces sont issues d'une recherche basée sur les données du DPE. Il est possible qu'il ne s'agisse pas exactement du même bien.
            </p>
            {ads.length > 0 && !isLoading && (
              <p className="mt-2 text-sm font-normal text-gray-500">
                {ads.length} annonce{ads.length > 1 ? 's' : ''} trouvée{ads.length > 1 ? 's' : ''}
              </p>
            )}
          </div>
        }
        width="90vw"
        maxWidth="1200px"
        height="auto"
        maxHeight="90vh"
      >
        <div className="flex h-full flex-col">
          {/* Content */}
          <div className="overflow-y-auto bg-white p-4 sm:p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              </div>
            ) : error ? (
              <div className="py-12 text-center">
                <p className="text-red-600">{error}</p>
              </div>
            ) : ads.length === 0 ? (
              <div className="py-12 text-center">
                <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">Aucune annonce trouvée pour ce DPE</p>
              </div>
            ) : (
              <div className="space-y-6">
                {ads.map((ad) => (
                  <div
                    key={ad.id}
                    className="overflow-hidden rounded-lg border border-gray-200 transition-shadow duration-200 hover:shadow-lg"
                  >
                    <div className="bg-white p-4">
                      {/* Image carousel */}
                      {ad.pictures.length > 0 && (
                        <div className="relative mb-4">
                          <img
                            src={ad.pictures[0]}
                            alt={ad.title}
                            className="h-64 w-full cursor-pointer rounded-lg object-cover"
                            onClick={() => openImageSlideshow(ad.pictures, 0)}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          {ad.pictures.length > 1 && (
                            <div className="absolute right-2 bottom-2 flex cursor-pointer items-center gap-1 rounded bg-black/70 px-2 py-1 text-xs text-white" onClick={() => openImageSlideshow(ad.pictures, 0)}>
                              <PhotoIcon className="h-4 w-4" />
                              {ad.pictures.length}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Title and basic info */}
                      <div className="mb-4">
                        <h4 className="mb-3 text-xl font-semibold text-gray-900">{ad.title}</h4>
                        <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                          <MapPinIcon className="h-4 w-4" />
                          <span>
                            {ad.cityName} ({ad.postalCode})
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-4 border-b border-gray-200 pb-4">
                        <h5 className="mb-2 text-sm font-semibold text-gray-700">Description</h5>
                        <p className="text-sm whitespace-pre-line text-gray-600">
                          {ad.description ?? 'Aucune description disponible'}
                        </p>
                      </div>

                      {/* Details grid */}
                      <div className="mb-4 grid grid-cols-2 gap-4 border-b border-gray-200 pb-4 md:grid-cols-5">
                        <div className="flex flex-col">
                          <span className="mb-1 text-xs text-gray-500">Transaction</span>
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                            ad.transactionType === 'sell'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {getTransactionTypeLabel(ad.transactionType)}
                          </span>
                        </div>

                        <div className="flex flex-col">
                          <span className="mb-1 text-xs text-gray-500">Prix</span>
                          <div className="flex items-center gap-1">
                            <CurrencyEuroIcon className="h-4 w-4 text-gray-600" />
                            <span className="font-semibold text-gray-900">
                              {formatPrice(ad.price)}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col">
                          <span className="mb-1 text-xs text-gray-500">Type</span>
                          <div className="flex items-center gap-1">
                            <HomeIcon className="h-4 w-4 text-gray-600" />
                            <span className="text-sm text-gray-900">
                              {getPropertyTypeLabel(ad.propertyType)}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col">
                          <span className="mb-1 text-xs text-gray-500">Surface</span>
                          <span className="text-sm font-medium text-gray-900">{numberWithSpaces(ad.area)} m²</span>
                        </div>

                        <div className="flex flex-col">
                          <span className="mb-1 text-xs text-gray-500">Pièces</span>
                          <span className="text-sm font-medium text-gray-900">{ad.room}</span>
                        </div>
                      </div>

                      {/* DPE and GES Section */}
                      {(ad.dpeClass || ad.gesClass) && (
                        <div className="mb-4 border-b border-gray-200 pb-4">
                          <h5 className="mb-3 text-sm font-semibold text-gray-700">
                            Diagnostic de Performance Énergétique
                          </h5>
                          <div className="flex items-center gap-8">
                            {ad.dpeClass && (
                              <div className="flex flex-col items-center">
                                <span className="mb-2 text-xs text-gray-500">DPE</span>
                                <div className="flex flex-col items-center">
                                  <Dpe dpe={ad.dpeClass} shape="rounded" size="md" />
                                  {ad.dpeValue !== undefined && (
                                    <div className="mt-2 text-xs text-gray-600">
                                      <span className="font-semibold">{Math.round(ad.dpeValue)}</span> kWh/m²/an
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {ad.gesClass && (
                              <div className="flex flex-col items-center">
                                <span className="mb-2 text-xs text-gray-500">GES</span>
                                <div className="flex flex-col items-center">
                                  <Ges ges={ad.gesClass} shape="rounded" size="md" />
                                  {ad.gesValue !== undefined && (
                                    <div className="mt-2 text-xs text-gray-600">
                                      <span className="font-semibold">{Math.round(ad.gesValue)}</span> kg CO₂/m²/an
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Publishers and dates */}
                      <div className="mb-4">
                        <div className="mb-2 flex flex-wrap gap-2">
                          {ad.publishers.map((publisher, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-800"
                            >
                              {publisher}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="h-3.5 w-3.5" />
                            <span>Créée le {formatDate(ad.adCreatedAt)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="h-3.5 w-3.5" />
                            <span>MAJ le {formatDate(ad.adUpdatedAt)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Links */}
                      <div className="space-y-2">
                        {ad.urls.map((url, idx) => (
                          <a
                            key={idx}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center justify-between rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-600 transition-colors duration-200 hover:bg-indigo-100"
                          >
                            <span className="truncate">{ad.publishers[idx] || 'Voir l\'annonce'}</span>
                            <LinkIcon className="ml-2 h-4 w-4 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Popup>

      {/* Image preview modal with slideshow */}
      <Popup
        isOpen={showImageModal}
        onClose={closeImageSlideshow}
        title={
          selectedImages.length > 1 ? (
            <div className="text-white">
              {currentImageIndex + 1} / {selectedImages.length}
            </div>
          ) : (
            ''
          )
        }
        width="fit-content"
        maxWidth="95vw"
        height="fit-content"
        maxHeight="95vh"
        disableCloseButton={false}
        additionalClass="!bg-black !p-4"
      >
        <div className="relative flex items-center justify-center">
          {/* Main image */}
          <img
            src={selectedImages[currentImageIndex]}
            alt={`Preview ${currentImageIndex + 1}`}
            className="max-h-[75vh] max-w-full rounded-lg object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />

          {/* Navigation arrows - only show if multiple images */}
          {selectedImages.length > 1 && (
            <>
              <button
                type="button"
                className="absolute top-1/2 left-4 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70 focus:outline-none"
                onClick={goToPreviousImage}
              >
                <ChevronLeftIcon className="h-8 w-8" />
              </button>

              <button
                type="button"
                className="absolute top-1/2 right-4 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70 focus:outline-none"
                onClick={goToNextImage}
              >
                <ChevronRightIcon className="h-8 w-8" />
              </button>
            </>
          )}
        </div>

        {/* Thumbnail strip - only show if multiple images */}
        {selectedImages.length > 1 && (
          <div className="mt-4 flex justify-center">
            <div className="scrollbar-hide flex max-w-full gap-2 overflow-x-auto px-2 py-2">
              {selectedImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`flex-shrink-0 overflow-hidden rounded-lg transition-all ${
                    idx === currentImageIndex
                      ? 'scale-110 ring-2 ring-white'
                      : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    className="h-14 w-14 object-cover sm:h-16 sm:w-16"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </Popup>
    </>
  );
};

export default AdsModal;

